import { useEffect, useState } from 'react'
import {
  getProjectPlanPath,
  getProjectPlanSiblings,
  getTree,
  ProjectPlanPathProps,
  isPathIncomplete,
  isPathStale,
} from '../../../../lib/functions/projectPlan'
import { useProjectPrivateContext } from '../../../context/projectContext'
import { WbsItemType } from '../../../../domain/entity/WbsItemEntity'

export type ProjectPlanBreadcrumb = {
  uuid: string
  name: string
  type: {
    iconUrl: string
  }
}

const mapDtoToModel = (src: any): ProjectPlanBreadcrumb => {
  return {
    uuid: src.uuid,
    name: src.wbsItem.name,
    type: {
      iconUrl: src.wbsItem.typeDto.iconUrl,
    },
  }
}
const flattenNestToArray = (src: any) => {
  const array: any[] = []
  const pushRecursive = (parent: any) => {
    array.push(parent)
    if (parent.child) {
      pushRecursive(parent.child)
    }
  }
  pushRecursive(src)
  return array
}

export const useProjectPlanBreadcrumbs = (
  rootProjectPlanUuid: string | undefined
): { breadcrumbs: ProjectPlanBreadcrumb[]; pathStale: boolean } => {
  const [breadcrumbs, setBreadcrumbs] = useState<ProjectPlanBreadcrumb[]>([])
  const [pathStale, setPathStale] = useState<boolean>(false)
  useEffect(() => {
    const fn = async () => {
      if (!rootProjectPlanUuid) {
        setBreadcrumbs([])
        setPathStale(false)
        return
      }
      const response = await getProjectPlanPath(rootProjectPlanUuid)
      const pathData = response.json as ProjectPlanPathProps

      setPathStale(
        isPathIncomplete(pathData, pathData?.wbsItem?.uuid) ||
          isPathStale(pathData)
      )

      const dtoArray = flattenNestToArray(pathData)
      setBreadcrumbs(dtoArray.map(dto => mapDtoToModel(dto)))
    }
    fn()
  }, [rootProjectPlanUuid])
  return { breadcrumbs, pathStale }
}

export const useProjectPlanSiblings = (projectPlanUuid: string) => {
  const [siblings, setSiblings] = useState<ProjectPlanBreadcrumb[]>([])
  useEffect(() => {
    const fn = async () => {
      const response = await getProjectPlanSiblings(projectPlanUuid)
      setSiblings(response.json)
    }
    fn()
  }, [projectPlanUuid])
  return siblings
}

// 直下の子として取得するデフォルトの WBS タイプ。呼び出し側で types を渡せば、その種別だけに絞れる。
const DEFAULT_CHILD_TYPES = [
  WbsItemType.WORKGROUP,
  WbsItemType.PROCESS,
  WbsItemType.DELIVERABLE_LIST,
  WbsItemType.DELIVERABLE,
  WbsItemType.TASK,
]

// 注意: types は useEffect の依存配列に入るため、安定参照を渡すこと
// (module スコープ定数 or useMemo)。レンダリングごとに新しい配列リテラルを渡すと再取得が無限に走る。
export const useProjectPlanDirectChildren = (
  projectPlanUuid: string | undefined,
  types: WbsItemType[] = DEFAULT_CHILD_TYPES
) => {
  const { projectUuid } = useProjectPrivateContext()
  const [children, setChildren] = useState<ProjectPlanBreadcrumb[]>([])
  useEffect(() => {
    const fn = async () => {
      const response = await getTree(types, projectPlanUuid, 1)
      setChildren(
        (response.json.children ?? []).map(child => ({
          uuid: child.uuid,
          name: child.wbsItem.displayName,
          type: child.wbsItem.typeDto,
        }))
      )
    }
    fn()
  }, [projectPlanUuid, projectUuid, types])
  return children
}
